import {
  KEAElement,
  KEAPaper,
  LinkLabelPosition,
  VariantGroup,
  VariantIcon,
  variantGroupsForType,
} from "@kea-mod/jointjs";
import { InteractionType } from "context/KEAGraphContext";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LABEL_RATIO } from "./InlineLabelEditor";

export type ContextMenuTarget = {
  view: joint.dia.ElementView | joint.dia.LinkView;
  isLink?: boolean;
  paper: KEAPaper;
  x: number;
  y: number;
};

interface Props {
  target: ContextMenuTarget;
  onClose: () => void;
  onRename: () => void;
  onProperties: () => void;
  pushPresent: () => void;
  addUserInteraction: (interactionType: InteractionType, interactionTime: number, payload?: any) => void;
}

const MARKER_SHAPES: Record<string, { d: string; filled?: boolean; half?: boolean }> = {
  arrow: { d: "M 9 0 L 3 -3 M 9 0 L 3 3" },
  arrow_filled: { d: "M 9 0 L 3 -3 L 3 3 Z", filled: true },
  arrow_empty: { d: "M 9 0 L 3 -3 L 3 3 Z" },
  arrow_halffilled: { d: "M 9 0 L 3 -3 L 3 0 Z", filled: true },
  diamond_empty: { d: "M 9 0 L 6 -3 L 3 0 L 6 3 Z" },
  diamond_filled: { d: "M 9 0 L 6 -3 L 3 0 L 6 3 Z", filled: true },
  circle_empty: { d: "M 9 0 A 3 3 0 1 1 3 0 A 3 3 0 1 1 9 0" },
  circle_filled: { d: "M 9 0 A 3 3 0 1 1 3 0 A 3 3 0 1 1 9 0", filled: true },
};

const LinkMarker = ({ type }: { type: string }): JSX.Element | null => {
  const shape = MARKER_SHAPES[type];
  if (!shape) return null;
  return <path d={shape.d} stroke="#020202" strokeWidth={1.2} fill={shape.filled ? "#020202" : "none"} />;
};

// The symbol names are not shown in the menu: in an exam they would give the answer away.
const VariantSymbol = ({ icon }: { icon: VariantIcon }): JSX.Element => (
  <svg width="22" height="22" viewBox="-11 -11 22 22" aria-hidden="true">
    {icon.rings.map((ring: VariantIcon["rings"][number], i: number) => (
      <circle
        key={i}
        r={ring.r}
        fill="none"
        stroke="#020202"
        strokeWidth={ring.width}
        strokeDasharray={ring.dash}
      />
    ))}
    {icon.diamond && <path d="M 0 -9 L 9 0 L 0 9 L -9 0 Z" fill="none" stroke="#020202" strokeWidth={1} />}
    {icon.box && <rect x={-9} y={-6} width={18} height={12} rx={2} fill="none" stroke="#020202" strokeWidth={1} />}
    {icon.line && (
      <path
        d={icon.line}
        stroke="#020202"
        strokeWidth={1.5}
        strokeDasharray={icon.dashed ? "3 2" : undefined}
        fill="none"
      />
    )}
    {icon.arrow && icon.arrow !== "none" && <LinkMarker type={icon.arrow} />}
    {icon.sourceArrow && icon.sourceArrow !== "none" && (
      <g transform="scale(-1 1)">
        <LinkMarker type={icon.sourceArrow} />
      </g>
    )}
    {icon.filledCircle && <circle r={icon.filledCircle} fill="#020202" />}
    {icon.cross && <path d="M -4 -4 L 4 4 M 4 -4 L -4 4" stroke="#020202" strokeWidth={1.5} fill="none" />}
    {icon.text && (
      <text textAnchor="middle" dominantBaseline="central" fontSize={11} fill="#020202">
        {icon.text}
      </text>
    )}
    {icon.marker && (
      <path
        d={icon.marker}
        transform={icon.markerTransform}
        stroke="#020202"
        strokeWidth={1}
        fill={icon.filled ? "#020202" : "none"}
      />
    )}
  </svg>
);

export const ElementContextMenu = ({
  target,
  onClose,
  onRename,
  onProperties,
  pushPresent,
  addUserInteraction,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const element = target.view.model as unknown as KEAElement;

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const escape = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [onClose]);

  const run = (action: () => void) => () => {
    action();
    onClose();
  };

  const [preview, setPreview] = useState<{ x: number; y: number } | null>(null);

  // Without a preview the menu does not show which end of the edge is the source.
  const labelPoint = (position: LinkLabelPosition) => {
    const linkView = target.view as joint.dia.LinkView;
    const point = linkView.getPointAtRatio(LABEL_RATIO[position]);
    return target.paper.localToPaperPoint(point.x, point.y);
  };

  const entries: { key: string; label: string; action: () => void; hover?: () => void }[] = [
    ...(target.isLink ? [] : [{ key: "rename", label: t("context_rename") as string, action: onRename }]),
    ...(target.isLink
      ? (target.paper.linkLabelPositions ?? []).map((position) => ({
          key: `label-${position}`,
          label: t(`context_label_${position}`) as string,
          action: () => target.paper.trigger("kealink:renamelabel", target.view, position),
          hover: () => setPreview(labelPoint(position)),
        }))
      : []),
    { key: "properties", label: t("context_properties"), action: onProperties },
    ...(target.isLink
      ? []
      : [
          { key: "front", label: t("context_to_front") as string, action: () => element.toFront() },
          { key: "back", label: t("context_to_back") as string, action: () => element.toBack() },
        ]),
    {
      key: "delete",
      label: t("context_delete"),
      action: () => {
        addUserInteraction(InteractionType.NODE_DELETED, Date.now(), element);
        element.remove();
      },
    },
  ];

  const groupsForType = target.isLink ? target.paper.linkVariants ?? [] : variantGroupsForType(element.get("type"));
  const variantGroups = groupsForType.filter((group) => group.appliesTo(element));

  const applyVariant = (group: VariantGroup, value: string) => {
    group.write(element, value);
    if (group.key === "linkVariant") target.paper.lastLinkVariant = value;
    pushPresent();
    addUserInteraction(InteractionType.NODE_EDITING_SAVE, Date.now(), element.toJSON());
  };

  return (
    <>
      {preview && (
        <div
          style={{
            position: "absolute",
            left: preview.x - 9,
            top: preview.y - 9,
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: "2px solid #00d1b2",
            background: "rgba(0, 209, 178, 0.25)",
            pointerEvents: "none",
            zIndex: 21,
          }}
        />
      )}
    <div
      ref={ref}
      className="box p-1"
      style={{ position: "absolute", left: target.x, top: target.y, zIndex: 20, minWidth: 170, maxWidth: 220 }}
    >
      {variantGroups.map((group) => {
        const current = group.read(element);
        return (
          <div key={group.key} className="mb-2">
            <p className="is-size-7 has-text-grey px-2">{t(group.labelKey)}</p>
            <div className="buttons are-small mb-0" style={{ gap: 2 }}>
              {group.options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-label={t(option.labelKey) as string}
                  className={`button is-small px-1 ${option.value === current ? "is-link is-light" : "is-white"}`}
                  onClick={() => applyVariant(group, option.value)}
                >
                  <VariantSymbol icon={option.icon} />
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {variantGroups.length > 0 && <hr className="my-1" />}
      {entries.map((entry) => (
        <button
          key={entry.key}
          type="button"
          className="button is-white is-small is-fullwidth is-justify-content-flex-start"
          onClick={run(entry.action)}
          onMouseEnter={() => entry.hover?.()}
          onMouseLeave={() => setPreview(null)}
        >
          {entry.label}
        </button>
      ))}
    </div>
    </>
  );
};
